import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, UtensilsCrossed } from 'lucide-react';
import { foodGallery, restaurantInfo } from '../config/siteConfig';

/**
 * "From the Kitchen" — editorial food photography collage.
 * Sits right after the menu so the dishes people just read about
 * are the dishes they see. Click any tile for a full-screen view.
 */
const FoodGallerySection = () => {
  const sectionRef = useRef(null);
  const dialogRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const images = foodGallery.images;
  const lightboxOpen = lightboxIndex !== null;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const close = useCallback(() => setLightboxIndex(null), []);
  const step = useCallback((dir) => {
    setLightboxIndex((prev) =>
      prev === null ? prev : (prev + dir + images.length) % images.length
    );
  }, [images.length]);

  // Focus management: move focus into the dialog on open, restore it on close
  useEffect(() => {
    if (!lightboxOpen) return;
    const previouslyFocused = document.activeElement;
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [lightboxOpen]);

  // Keyboard navigation + focus trap + scroll lock while the lightbox is open
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'Tab') {
        // Minimal trap: cycle focus among the dialog's buttons (close/prev/next)
        const buttons = dialogRef.current
          ? Array.from(dialogRef.current.querySelectorAll('button'))
          : [];
        if (buttons.length === 0) return;
        e.preventDefault();
        const idx = buttons.indexOf(document.activeElement);
        const next = e.shiftKey
          ? (idx <= 0 ? buttons.length - 1 : idx - 1)
          : (idx === -1 || idx === buttons.length - 1 ? 0 : idx + 1);
        buttons[next].focus();
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIndex, close, step]);

  // Collage span pattern: first tile is the 2x2 feature; one mid tile and
  // the last tile go wide so the 11-photo grid fills a clean 4x4 without
  // holes (4 + 2 + 2 + 8 singles = 16 cells). grid-flow-dense backfills
  // gracefully if the team adds or removes photos in siteConfig.
  const tileSpan = (i) => {
    if (i === 0) return 'col-span-2 row-span-2';
    if (i === 5 || i === images.length - 1) return 'sm:col-span-2';
    return '';
  };

  return (
    <section id="gallery" ref={sectionRef} className="py-20 lg:py-24 bg-[#FDF6E9] texture-paper relative overflow-hidden">
      {/* Decorative glows to match the rest of the site */}
      <div className="hidden md:block absolute top-10 right-0 w-96 h-96 bg-[#C9A227]/5 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-0 left-0 w-80 h-80 bg-[#D4A574]/10 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-5">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#C9A227] font-semibold tracking-widest text-sm uppercase flex items-center justify-center gap-2">
            <UtensilsCrossed className="w-4 h-4" />
            {foodGallery.subtitle}
          </span>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#2C2416] tracking-wide mt-3 leading-[0.95]">
            {foodGallery.title}
          </h2>
          <p className="mt-4 text-lg text-[#2C2416]/75 max-w-xl mx-auto">
            Straight from our kitchen. Come hungry.
          </p>
        </div>

        {/* Collage grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[150px] sm:auto-rows-[170px] lg:auto-rows-[190px] gap-3 sm:gap-4 grid-flow-dense">
          {images.map((image, i) => (
            <button
              key={image.src}
              onClick={() => setLightboxIndex(i)}
              className={`group relative overflow-hidden rounded-lg shadow-warm hover:shadow-warm-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-[#C9A227]/60 transition-all duration-500 ${tileSpan(i)} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: isVisible ? `${Math.min(i * 70, 600)}ms` : '0ms' }}
              aria-label={`View photo: ${image.caption}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover img-vintage transition-transform duration-700 ease-out group-hover:scale-110"
              />
              {/* Caption gradient on hover (always visible on the feature tile) */}
              <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1a1612]/85 via-[#1a1612]/30 to-transparent transition-opacity duration-300 ${
                i === 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              }`}>
                <p className={`px-4 pb-3 pt-10 text-left font-display tracking-wide text-[#FDF6E9] ${i === 0 ? 'text-2xl sm:text-3xl' : 'text-lg'}`}>
                  {image.caption}
                </p>
              </div>
              {/* Gold corner accent on hover */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#C9A227]/60 rounded-lg transition-colors duration-300 pointer-events-none" />
            </button>
          ))}
        </div>

        {/* CTA under the grid */}
        <div className={`text-center mt-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <a
            href={restaurantInfo.orderOnlineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-retro inline-flex items-center gap-3"
          >
            HUNGRY YET? ORDER NOW
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="fixed inset-0 z-[100] bg-[#1a1612]/95 backdrop-blur-sm flex items-center justify-center"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label={images[lightboxIndex].caption}
        >
          {/* Close */}
          <button
            onClick={close}
            className="absolute top-5 right-5 text-[#FDF6E9]/70 hover:text-[#C9A227] transition-colors z-10"
            aria-label="Close photo viewer"
          >
            <X className="w-9 h-9" />
          </button>

          {/* Prev / Next */}
          <button
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-3 sm:left-6 text-[#FDF6E9]/70 hover:text-[#C9A227] transition-colors z-10 p-2"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-3 sm:right-6 text-[#FDF6E9]/70 hover:text-[#C9A227] transition-colors z-10 p-2"
            aria-label="Next photo"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          {/* Image + caption */}
          <figure className="max-w-5xl w-full px-14 sm:px-20" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[lightboxIndex].src}
              alt={images[lightboxIndex].alt}
              className="w-full max-h-[78vh] object-contain rounded-lg shadow-2xl"
            />
            <figcaption className="mt-4 text-center">
              <span className="font-display text-2xl tracking-wide text-[#FDF6E9]">
                {images[lightboxIndex].caption}
              </span>
              <span className="block mt-1 text-sm text-[#FDF6E9]/50">
                {lightboxIndex + 1} / {images.length} — {restaurantInfo.name}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
};

export default FoodGallerySection;
